import type { InGameImage } from '~/components/live-casino/types';
import { EnhancedGame } from '~/components/casino-shared/types';
import { gameLiveCasinoDataSelector, useLiveCasinoSelector } from '~/components/live-casino/ducks/selectors';
import { selectEnableLiveCasino3 } from '~/components/brand/ducks/selectors';
import useCasinoLocation from '~/hooks/use-casino-location';

const imageDescriptors: InGameImage = {
	small: '180w',
	medium: '240w',
	large: '480w',
	xLarge: '640w'
};

const useIngameImage = (game: Pick<EnhancedGame, 'liveFeedCode' | 'provider'>, forceStaticImage?: boolean) => {
	const gameLiveCasinoData = useLiveCasinoSelector(state => gameLiveCasinoDataSelector(state, game));
	const enableLiveCasino3 = useLiveCasinoSelector(selectEnableLiveCasino3);
	const { isCasinoExploreLocation, isLiveCasinoExploreLocation, isSubmenuLocation } = useCasinoLocation();

	if (
		!enableLiveCasino3 ||
		forceStaticImage ||
		!(isCasinoExploreLocation || isLiveCasinoExploreLocation || isSubmenuLocation)
	) {
		return {
			srcSet: null,
			formats: null
		};
	}

	const getFileExtension = (fileNames: string[]) => fileNames[0]?.match(/\.(\w+)$/i);

	const srcSet =
		gameLiveCasinoData?.status?.inGameImage &&
		Object.entries(gameLiveCasinoData.status.inGameImage)
			// get rid of small since it's not visible enough
			.filter(([key], _, array) => {
				// some providers return only one image
				if (array.length === 1) {
					return true;
				}

				return key === 'xLarge';
			})
			.map(([key, value]: [string, string]) => `${value} ${imageDescriptors[key as keyof InGameImage]}`)
			.join(', ');

	const extension = gameLiveCasinoData?.status?.inGameImage
		? getFileExtension(Object.values(gameLiveCasinoData?.status?.inGameImage) as string[])?.[1]
		: null;

	const formats = extension
		? [
				{
					srcSet,
					type: `image/${extension.replace('jpg', 'jpeg')}`
				}
			]
		: null;

	return { srcSet, formats };
};

export default useIngameImage;
